import Styles from './visitRequestCard.module.css';
import React from 'react';
import { IoCalendarOutline } from 'react-icons/io5';
import { TOPIC_LABELS } from '../types';

// サロン見学の申込カード。
// 学生アプリの申込フォーム(visitSettings が有効な求人)から申し込むと、本文「サロン見学を申し込みました」の
// メッセージに type: 'visit' と visit(希望店舗・希望日時・気になっていること)が付いて届く。
// 学生アプリではこれをカードで表示しているが、サロンWebのメッセージは本文しか出しておらず、
// 申込内容は応募者詳細の「希望内容」タブでしか見られなかった。会話の中で確認できるよう、
// メッセージ画面(応募者詳細・応募管理)で同じ内容をカードとして表示する。
export type VisitRequestMessage = {
  preferredStoreName: string | null;
  preferredDates: Date[];
  interestedTopics: string[];
};

function toDate(value: any): Date | null {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

// messages ドキュメントから申込カードの内容を取り出す。申込カードでなければ null。
export function parseVisitRequestMessage(data: any): VisitRequestMessage | null {
  if (data?.type !== 'visit' || !data?.visit) return null;
  const visit = data.visit;
  return {
    preferredStoreName: typeof visit.preferredStoreName === 'string' && visit.preferredStoreName ? visit.preferredStoreName : null,
    preferredDates: (Array.isArray(visit.preferredDates) ? visit.preferredDates : [])
      .map(toDate)
      .filter((d: Date | null): d is Date => d !== null),
    interestedTopics: Array.isArray(visit.interestedTopics) ? visit.interestedTopics.filter((t: any) => typeof t === 'string') : [],
  };
}

function formatDateTime(d: Date): string {
  const week = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${d.getMonth() + 1}/${d.getDate()}(${week}) ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const VisitRequestCard: React.FC<{ visit: VisitRequestMessage }> = ({ visit }) => (
  <div className={Styles.card}>
    <div className={Styles.header}>
      <IoCalendarOutline />
      <span>サロン見学を申し込みました</span>
    </div>
    <div className={Styles.body}>
      {visit.preferredStoreName && (
        <div className={Styles.field}>
          <p className={Styles.label}>希望店舗</p>
          <p className={Styles.value}>{visit.preferredStoreName}</p>
        </div>
      )}
      {visit.preferredDates.length > 0 && (
        <div className={Styles.field}>
          <p className={Styles.label}>希望日時</p>
          {visit.preferredDates.map((d, i) => (
            <p key={i} className={Styles.value}>第{i + 1}希望：{formatDateTime(d)}</p>
          ))}
        </div>
      )}
      {visit.interestedTopics.length > 0 && (
        <div className={Styles.field}>
          <p className={Styles.label}>気になっていること</p>
          <div className={Styles.topics}>
            {visit.interestedTopics.map((t) => (
              <span key={t} className={Styles.topic}>{TOPIC_LABELS[t] ?? t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

export default VisitRequestCard;

// ── 会社説明会・クーポンの申込カード ──
// 学生アプリの「説明会に参加する」「クーポンを申し込む」で、メッセージに type: 'infoSession' / 'coupon' と
// event(どの説明会・クーポンか: 説明会名・日時・場所 / 施術名・価格)が付いて届く。
export type EventRequestMessage = {
  kind: 'infoSession' | 'coupon';
  name: string;
  startAt: Date | null;
  place: string;
  treatment: string;
  priceAfter: number;
  priceBefore: number;
};

export function parseEventRequestMessage(data: any): EventRequestMessage | null {
  if ((data?.type !== 'infoSession' && data?.type !== 'coupon') || !data?.event) return null;
  const e = data.event;
  return {
    kind: data.type,
    name: typeof e.name === 'string' ? e.name : '',
    startAt: toDate(e.startAt),
    place: typeof e.place === 'string' ? e.place : '',
    treatment: typeof e.treatment === 'string' ? e.treatment : '',
    priceAfter: Number(e.priceAfter) || 0,
    priceBefore: Number(e.priceBefore) || 0,
  };
}

export const EventRequestCard: React.FC<{ event: EventRequestMessage }> = ({ event }) => (
  <div className={`${Styles.card} ${Styles.eventCard}`}>
    <div className={`${Styles.header} ${Styles.eventHeader}`}>
      <IoCalendarOutline />
      <span>{event.kind === 'infoSession' ? '会社説明会に申し込みました' : 'クーポンを申し込みました'}</span>
    </div>
    <div className={Styles.body}>
      {event.kind === 'infoSession' ? (
        <>
          <div className={Styles.field}>
            <p className={Styles.label}>会社説明会</p>
            <p className={Styles.value}>{event.name || '-'}</p>
          </div>
          {event.startAt && (
            <div className={Styles.field}>
              <p className={Styles.label}>日時</p>
              <p className={Styles.value}>{formatDateTime(event.startAt)}</p>
            </div>
          )}
          {event.place && (
            <div className={Styles.field}>
              <p className={Styles.label}>場所</p>
              <p className={Styles.value}>{event.place}</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className={Styles.field}>
            <p className={Styles.label}>クーポン</p>
            <p className={Styles.value}>{event.treatment || '-'}</p>
          </div>
          {!!event.priceAfter && (
            <div className={Styles.field}>
              <p className={Styles.label}>学生価格</p>
              <p className={Styles.value}>
                ¥{event.priceAfter.toLocaleString('ja-JP')}
                {!!event.priceBefore && <span className={Styles.priceBefore}>（通常 ¥{event.priceBefore.toLocaleString('ja-JP')}）</span>}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  </div>
);
